import React, {useEffect, useMemo, useRef, useState} from 'react';
import usePageTitle from '../hooks/usePageTitle';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import PublicButton from '../components/PublicButton';

// Deterministic PRNG so the chart renders identically on every load.
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildSeries({n, baseFn, jitter, seed, drift = 0}) {
  const rnd = mulberry32(seed);
  const out = new Array(n);
  let acc = 0;
  for (let i = 0; i < n; i++) {
    acc = acc * 0.85 + (rnd() - 0.5) * 2 * jitter;
    out[i] = baseFn(i / (n - 1)) + acc + drift * (i / (n - 1));
  }
  return out;
}

// Top-6 monthly RMSE rankings from the Belgian proof-of-concept (CIGRE 2026, Fig. 7).
// Each column is one month, top row is rank 1 (best). "ENS" is the Predico ensemble.
// "*" marks the contracted forecaster on each track (F29* for wind, F12* for solar).
const RANKING_COLORS = {
  ENS:    {bg: '#05788a', fg: '#FFFFFF'}, // brand teal — the visual anchor
  'F29*': {bg: '#C26878', fg: '#FFFFFF'}, // operational forecast (wind)
  'F12*': {bg: '#B57080', fg: '#FFFFFF'}, // operational forecast (solar)
  F29:    {bg: '#DCBFC9', fg: '#0F172A'},
  F27:    {bg: '#E8E2D8', fg: '#0F172A'},
  F5:     {bg: '#F0D8D2', fg: '#0F172A'},
  F35:    {bg: '#DCD2E5', fg: '#0F172A'},
  F13:    {bg: '#C8DEC2', fg: '#0F172A'},
  F2:     {bg: '#C8C4D0', fg: '#0F172A'},
  F19:    {bg: '#C5C4D8', fg: '#0F172A'},
  F8:     {bg: '#E0D8A0', fg: '#0F172A'},
  F14:    {bg: '#A8BCD2', fg: '#0F172A'},
  F37:    {bg: '#CFCFCF', fg: '#0F172A'},
  F10:    {bg: '#B8CCDF', fg: '#0F172A'},
  F15:    {bg: '#E8C0C0', fg: '#0F172A'},
  F11:    {bg: '#EDD8BA', fg: '#0F172A'},
  F18:    {bg: '#D8E2EC', fg: '#0F172A'},
  F22:    {bg: '#D8C5B0', fg: '#0F172A'},
  F9:     {bg: '#C8DEDC', fg: '#0F172A'},
  F7:     {bg: '#DCE0B5', fg: '#0F172A'},
};

const WIND_RANKING = {
  months: ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'],
  ranks: [
    ['ENS',  'F29*','F27', 'ENS', 'ENS', 'F5',  'ENS', 'ENS', 'ENS', 'F5',   'F5',  'F2'  ],
    ['F27',  'ENS', 'ENS', 'F5',  'F35', 'ENS', 'F5',  'F5',  'F5',  'ENS',  'ENS', 'ENS' ],
    ['F35',  'F5',  'F19', 'F35', 'F19', 'F8',  'F35', 'F14', 'F27', 'F2',   'F19', 'F5'  ],
    ['F13',  'F27', 'F5',  'F29*','F14', 'F27', 'F37', 'F8',  'F13', 'F37',  'F2',  'F29*'],
    ['F5',   'F14', 'F35', 'F27', 'F27', 'F14', 'F13', 'F29*','F29*','F14',  'F29*','F14' ],
    ['F29*', 'F19', 'F14', 'F19', 'F37', 'F35', 'F8',  'F27', 'F37', 'F29*', 'F37', 'F13' ],
  ],
};

const SOLAR_RANKING = {
  months: ['APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV'],
  ranks: [
    ['F12*','ENS', 'ENS', 'ENS', 'F7',  'ENS',  'F2',   'F2'  ],
    ['ENS', 'F12*','F12*','F10', 'ENS', 'F29',  'ENS',  'ENS' ],
    ['F10', 'F15', 'F2',  'F12*','F12*','F22',  'F10',  'F12*'],
    ['F14', 'F11', 'F10', 'F18', 'F11', 'F12*', 'F9',   'F27' ],
    ['F29', 'F18', 'F11', 'F11', 'F2',  'F15',  'F12*', 'F15' ],
    ['F22', 'F5',  'F15', 'F5',  'F14', 'F10',  'F14',  'F7'  ],
  ],
};

function smooth(values, window = 3) {
  const out = new Array(values.length);
  for (let i = 0; i < values.length; i++) {
    let s = 0, c = 0;
    for (let k = -window; k <= window; k++) {
      const j = i + k;
      if (j >= 0 && j < values.length) { s += values[j]; c++; }
    }
    out[i] = s / c;
  }
  return out;
}

function toPath(values, {width, height, padX, padY, yMin, yMax}) {
  const n = values.length;
  const xStep = (width - 2 * padX) / (n - 1);
  const span = yMax - yMin || 1;
  return values.map((v, i) => {
    const x = padX + i * xStep;
    const y = padY + (1 - (v - yMin) / span) * (height - 2 * padY);
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
}

function toAreaPath(lower, upper, {width, height, padX, padY, yMin, yMax}) {
  const n = lower.length;
  const xStep = (width - 2 * padX) / (n - 1);
  const span = yMax - yMin || 1;
  const top = upper.map((v, i) => {
    const x = padX + i * xStep;
    const y = padY + (1 - (v - yMin) / span) * (height - 2 * padY);
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const bottom = lower.map((v, i) => {
    const x = padX + (n - 1 - i) * xStep;
    const y = padY + (1 - (lower[n - 1 - i] - yMin) / span) * (height - 2 * padY);
    return `L${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return `${top} ${bottom} Z`;
}

function ForecastPanel({title, kind}) {
  const W = 640, H = 220, padX = 28, padY = 16;
  const N = 96; // quarter-hour points across a day

  const series = useMemo(() => {
    if (kind === 'expectation') {
      // Clean: smooth diurnal-ish curve, all forecasters hug observation.
      const base = (t) => 0.55 + 0.35 * Math.sin(Math.PI * t - 0.4);
      const observed = smooth(buildSeries({n: N, baseFn: base, jitter: 0.015, seed: 11}), 2);
      const forecasters = Array.from({length: 7}, (_, k) =>
        smooth(buildSeries({n: N, baseFn: base, jitter: 0.02, seed: 100 + k * 17}), 3)
      );
      const lower = forecasters[0].map((_, i) =>
        Math.min(...forecasters.map(f => f[i])) - 0.015);
      const upper = forecasters[0].map((_, i) =>
        Math.max(...forecasters.map(f => f[i])) + 0.015);
      const ensemble = observed.map((v, i) => {
        const m = forecasters.reduce((s, f) => s + f[i], 0) / forecasters.length;
        return 0.5 * v + 0.5 * m;
      });
      return {observed, forecasters, ensemble, lower, upper};
    }
    // Reality: messy intermittent curve, forecasts diverge, ensemble still tracks.
    const base = (t) => {
      const ramp = 0.35 + 0.55 * Math.pow(t, 1.4);
      const dip = -0.45 * Math.exp(-Math.pow((t - 0.42) / 0.07, 2));
      const bump = 0.25 * Math.exp(-Math.pow((t - 0.72) / 0.05, 2));
      return ramp + dip + bump;
    };
    const observed = smooth(buildSeries({n: N, baseFn: base, jitter: 0.05, seed: 7}), 1);
    const forecasters = Array.from({length: 8}, (_, k) =>
      smooth(buildSeries({
        n: N,
        baseFn: (t) => base(t) + (k - 3.5) * 0.025 * Math.sin(2.5 * Math.PI * t + k),
        jitter: 0.07 + (k % 3) * 0.02,
        seed: 200 + k * 31,
        drift: (k - 3.5) * 0.04,
      }), 2)
    );
    const lower = forecasters[0].map((_, i) =>
      Math.min(...forecasters.map(f => f[i])) - 0.02);
    const upper = forecasters[0].map((_, i) =>
      Math.max(...forecasters.map(f => f[i])) + 0.02);
    const ensemble = smooth(observed.map((v, i) => {
      const m = forecasters.reduce((s, f) => s + f[i], 0) / forecasters.length;
      return 0.6 * v + 0.4 * m;
    }), 3);
    return {observed, forecasters, ensemble, lower, upper};
  }, [kind]);

  const all = [
    ...series.observed,
    ...series.ensemble,
    ...series.lower,
    ...series.upper,
    ...series.forecasters.flat(),
  ];
  const yMin = Math.min(...all) - 0.05;
  const yMax = Math.max(...all) + 0.05;
  const opts = {width: W, height: H, padX, padY, yMin, yMax};

  const forecasterColors = [
    'rgba(116,205,214,0.55)',
    'rgba(58,171,184,0.55)',
    'rgba(29,138,155,0.55)',
    'rgba(170,227,233,0.55)',
    'rgba(7,98,115,0.55)',
    'rgba(10,79,93,0.55)',
    'rgba(116,205,214,0.4)',
    'rgba(58,171,184,0.4)',
  ];

  return (
    <div className="w-full">
      <div className="flex items-baseline justify-between mb-2 px-1">
        <h3 className="text-xs uppercase tracking-[0.18em] font-medium text-brand-600 dark:text-brand-400">
          {title}
        </h3>
        <span className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-gray-500 font-mono">
          day-ahead · 15-min
        </span>
      </div>
      <div className="relative rounded-md border border-slate-900/10 dark:border-gray-100/10 bg-slate-50/40 dark:bg-gray-700/40">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none" role="img"
             aria-label={`Illustrative ${title.toLowerCase()} forecast chart`}>
          {/* Gridlines */}
          {[0.25, 0.5, 0.75].map(g => (
            <line key={g}
                  x1={padX} x2={W - padX}
                  y1={padY + g * (H - 2 * padY)}
                  y2={padY + g * (H - 2 * padY)}
                  className="stroke-slate-900/5 dark:stroke-gray-100/5"
                  strokeWidth="1"/>
          ))}
          {/* Forecast spread band */}
          <path d={toAreaPath(series.lower, series.upper, opts)}
                className="fill-brand-400/15 dark:fill-brand-400/15"/>
          {/* Individual forecasters */}
          {series.forecasters.map((f, i) => (
            <path key={i}
                  d={toPath(f, opts)}
                  fill="none"
                  stroke={forecasterColors[i % forecasterColors.length]}
                  strokeWidth="1"
                  strokeLinejoin="round"
                  strokeLinecap="round"/>
          ))}
          {/* Ensemble */}
          <path d={toPath(series.ensemble, opts)}
                fill="none"
                className="stroke-accent-500"
                strokeWidth="2.25"
                strokeLinejoin="round"
                strokeLinecap="round"/>
          {/* Observed */}
          <path d={toPath(series.observed, opts)}
                fill="none"
                className="stroke-slate-900 dark:stroke-white"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"/>
        </svg>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-gray-400 px-1">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-[2px] bg-slate-900 dark:bg-white"/>
          Observed
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-[2px] bg-accent-500"/>
          Predico ensemble
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-[2px] bg-brand-400/60"/>
          Individual forecasters
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-2 bg-brand-400/20 border border-brand-400/30"/>
          Spread
        </span>
      </div>
    </div>
  );
}

function PainPoint({label, title, body}) {
  return (
    <div className="py-6 px-3 sm:px-4">
      <div className="text-[11px] uppercase tracking-[0.18em] text-brand-600 dark:text-brand-400 font-medium">
        {label}
      </div>
      <h3 className="mt-2 text-lg font-medium text-slate-900 dark:text-white tracking-tight">
        {title}
      </h3>
      <p className="mt-2 text-sm text-slate-600 dark:text-gray-300 leading-relaxed">
        {body}
      </p>
    </div>
  );
}

function RankingTabs() {
  const [tab, setTab] = useState('wind');
  const tabs = [
    {id: 'wind', label: 'Wind power', data: WIND_RANKING},
    {id: 'solar', label: 'Solar power', data: SOLAR_RANKING},
  ];
  const active = tabs.find(t => t.id === tab) || tabs[0];
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-3 px-1 border-b border-slate-900/10 dark:border-gray-100/10">
        <div className="flex items-center gap-1" role="tablist" aria-label="Forecast use case">
          {tabs.map(t => {
            const isActive = t.id === tab;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setTab(t.id)}
                className={`relative px-3 py-2 text-xs uppercase tracking-[0.18em] font-medium transition ${
                  isActive
                    ? 'text-brand-600 dark:text-brand-400'
                    : 'text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200'
                }`}
              >
                {t.label}
                {isActive && (
                  <span
                    aria-hidden="true"
                    className="absolute left-0 right-0 -bottom-px h-[2px] bg-brand-600 dark:bg-brand-400"
                  />
                )}
              </button>
            );
          })}
        </div>
        <span className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-gray-500 font-mono">
          RMSE rank · monthly
        </span>
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-900/10 dark:border-gray-100/10 bg-slate-50/40 dark:bg-gray-700/40 p-3">
        <div
          className="grid gap-[2px]"
          style={{
            gridTemplateColumns: `28px repeat(${active.data.months.length}, minmax(40px, 1fr))`,
            minWidth: `${28 + active.data.months.length * 44}px`,
          }}
        >
          {active.data.ranks.map((row, ri) => (
            <React.Fragment key={ri}>
              <div className="flex items-center justify-end pr-2 text-[10px] font-mono text-slate-500 dark:text-gray-400 tabular-nums">
                {ri + 1}
              </div>
              {row.map((id, ci) => {
                const c = RANKING_COLORS[id] || {bg: '#cbd5e1', fg: '#0F172A'};
                const isContracted = id.endsWith('*');
                return (
                  <div
                    key={ci}
                    className={`flex items-center justify-center h-7 sm:h-8 rounded-[2px] text-[10px] font-mono ${
                      isContracted ? 'font-bold' : 'font-medium'
                    }`}
                    style={{backgroundColor: c.bg, color: c.fg}}
                    title={`Rank ${ri + 1} · ${active.data.months[ci]} · ${id}`}
                  >
                    {id}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
          <div/>
          {active.data.months.map((m) => (
            <div key={m}
                 className="text-[10px] uppercase tracking-wider font-mono text-slate-500 dark:text-gray-400 text-center pt-1.5">
              {m}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function useInView({rootMargin = '0px 0px -10% 0px'} = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            obs.unobserve(entry.target);
          }
        });
      },
      {rootMargin, threshold: 0.05}
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin]);
  return [ref, inView];
}

function Reveal({as: Tag = 'div', className = '', id, children}) {
  const [ref, inView] = useInView();
  return (
    <Tag
      ref={ref}
      id={id}
      className={`motion-safe:transition-all motion-safe:duration-700 motion-safe:ease-out ${
        inView ? 'opacity-100 translate-y-0' : 'motion-safe:opacity-0 motion-safe:translate-y-3'
      } ${className}`}
    >
      {children}
    </Tag>
  );
}

const SECTION_LINKS = [
  {id: 'problems', label: 'Motivation'},
  {id: 'live', label: 'Expectation vs reality'},
  {id: 'how', label: 'How it works'},
  {id: 'results', label: 'Operational results'},
  {id: 'references', label: 'References'},
];

function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  const preferredRef = useRef(null);
  const expireRef = useRef(0);

  useEffect(() => {
    let raf = 0;
    function update() {
      raf = 0;
      const offset = 100; // sticky header + a little buffer
      const scrollY = window.scrollY;
      const viewportH = window.innerHeight;
      const docH = document.documentElement.scrollHeight;

      // Position walk: last section whose top has crossed the offset line.
      let walked = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - offset <= 0) {
          walked = id;
        } else {
          break;
        }
      }

      // At document bottom: pick the visible section whose top is closest to
      // the offset line. Handles short pages where later sections can never
      // scroll high enough for the walk to "cross" them, without skipping
      // intermediate sections.
      if (scrollY + viewportH >= docH - 4) {
        let bestId = walked;
        let bestDist = Infinity;
        for (const id of ids) {
          const el = document.getElementById(id);
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > viewportH) continue;
          const dist = Math.abs(rect.top - offset);
          if (dist < bestDist) {
            bestDist = dist;
            bestId = id;
          }
        }
        walked = bestId;
      }

      // Click-driven preferred override: keep the clicked section highlighted
      // through the smooth scroll and while we sit at the page bottom.
      const preferred = preferredRef.current;
      if (preferred) {
        const inWindow = Date.now() < expireRef.current;
        const el = document.getElementById(preferred);
        if (el) {
          const rect = el.getBoundingClientRect();
          // Arrived = section's top has landed in the band the smooth-scroll
          // target lands at (just below the offset line). A wider tolerance
          // would fire mid-scroll, before walked recognises the section,
          // causing a flicker back to the previous one.
          const arrived = rect.top <= offset && rect.top > offset - 80;
          // Gone = section is fully out of the viewport in either direction
          // (scrolled past it going down, or above-the-fold going up).
          const gone = rect.bottom < 0 || rect.top > viewportH;
          if (arrived) {
            preferredRef.current = null;
          } else if (!inWindow && gone) {
            preferredRef.current = null;
          } else {
            setActive(preferred);
            return;
          }
        } else {
          preferredRef.current = null;
        }
      }

      setActive(walked);
    }
    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(update);
    }
    update();
    window.addEventListener('scroll', onScroll, {passive: true});
    window.addEventListener('resize', onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [ids]);

  const setActiveManual = (id) => {
    preferredRef.current = id;
    expireRef.current = Date.now() + 1500;
    setActive(id);
  };

  return [active, setActiveManual];
}

function smoothJumpTo(e, id) {
  e.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
  window.scrollTo({top, behavior: 'smooth'});
  if (window.history && window.history.replaceState) {
    window.history.replaceState(null, '', `#${id}`);
  }
}

function SectionSidebarNav() {
  const ids = useMemo(() => SECTION_LINKS.map((s) => s.id), []);
  const [active, setActiveManual] = useActiveSection(ids);
  const handleClick = (e, id) => {
    setActiveManual(id);
    smoothJumpTo(e, id);
  };
  return (
    <nav aria-label="On this page" className="text-sm">
      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-gray-500 font-mono mb-4">
        On this page
      </div>
      <ul className="space-y-2 border-l border-slate-900/10 dark:border-gray-100/10">
        {SECTION_LINKS.map((s) => {
          const isActive = active === s.id;
          return (
            <li key={s.id} className="relative">
              {isActive && (
                <span aria-hidden="true"
                      className="absolute -left-px top-0 bottom-0 w-[2px] bg-brand-600 dark:bg-brand-400"/>
              )}
              <a
                href={`#${s.id}`}
                onClick={(e) => handleClick(e, s.id)}
                className={`block pl-3 py-1 transition ${
                  isActive
                    ? 'text-brand-600 dark:text-brand-400 font-medium'
                    : 'text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {s.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function SectionJumpNavMobile() {
  return (
    <nav aria-label="On this page"
         className="lg:hidden mt-10 pt-6 border-t border-slate-900/10 dark:border-gray-100/10">
      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-gray-500 font-mono mb-3">
        On this page
      </div>
      <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
        {SECTION_LINKS.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              onClick={(e) => smoothJumpTo(e, s.id)}
              className="text-slate-700 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition underline-offset-4 hover:underline"
            >
              {s.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function Metric({value, label, type, description, comparison}) {
  return (
    <div className="px-6 py-6">
      <div className="flex items-baseline gap-x-3 flex-wrap">
        <div className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 dark:text-white font-mono tabular-nums">
          {value}
        </div>
        {label && (
          <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-gray-400">
            {label}
          </div>
        )}
      </div>
      {type && (
        <div className="mt-3 text-[11px] uppercase tracking-[0.12em] font-medium text-slate-700 dark:text-gray-200">
          {type}
        </div>
      )}
      {description && (
        <div className="mt-1 text-xs text-slate-500 dark:text-gray-400 leading-relaxed">
          {description}
        </div>
      )}
      {comparison && (
        <div className="text-xs text-slate-500 dark:text-gray-400 leading-relaxed">
          {comparison}
        </div>
      )}
    </div>
  );
}

export default function About() {
  usePageTitle('About');
  return (
    <div className="min-h-screen bg-surface dark:bg-gray-870 text-slate-900 dark:text-gray-100 flex flex-col">
      <PublicNav/>

      <main className="flex-1 w-full max-w-7xl 2xl:max-w-[1600px] mx-auto px-6 pt-12 pb-20">
        <div className="lg:grid lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-x-12 xl:gap-x-16">
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <SectionSidebarNav/>
            </div>
          </aside>
          <div className="min-w-0">
        {/* Hero */}
        <Reveal as="section">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-600 dark:text-brand-400 font-medium">
            Why Predico
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.05] -tracking-[0.02em] text-slate-900 dark:text-white">
            Better forecasts come from <span className="italic font-semibold text-brand-600 dark:text-brand-400">many models</span>,<br className="hidden sm:block"/>
            not one supplier.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 dark:text-gray-300 max-w-3xl leading-relaxed">
            Grid operators rely on accurate renewable-power forecasts for secure, low-cost operation.
            Today those forecasts are bought from one or two suppliers under long bilateral contracts.
            That model is slow to procure, hard to compare, and weak on incentives once signed.
            Predico replaces it with an open market where many forecasters compete every day,
            and their submissions are combined into a single, more accurate operational forecast.
          </p>
          <SectionJumpNavMobile/>
        </Reveal>

        {/* Pain points */}
        <Reveal as="section" id="problems" className="mt-16 scroll-mt-24">
          <h2 className="text-[13px] uppercase tracking-[0.18em] text-slate-700 dark:text-gray-200 font-medium">
            The problems we set out to solve
          </h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-900/10 dark:divide-gray-100/10 border-y border-slate-900/10 dark:border-gray-100/10">
            <PainPoint
              label="01 · Procurement"
              title="Bilateral contracts are slow and lock you in."
              body="Selecting a supplier means market research, sales cycles, legal review, and short trials that most commercial forecasters consider unattractive. Switching mid-contract is costly, so the market settles on a handful of established players."
            />
            <PainPoint
              label="02 · Incentives"
              title="Once contracted, the pressure to improve fades."
              body="A signed contract removes the day-to-day incentive for a supplier to keep iterating. We rank forecasters on objective live performance and pay them accordingly, every day and every month."
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-900/10 dark:divide-gray-100/10 border-b border-slate-900/10 dark:border-gray-100/10">
            <PainPoint
              label="03 · Trust gap"
              title="Backtests don't predict live performance."
              body="In one year of live operation we saw wide gaps between vendors' reported backtest results and what they delivered in production. We stopped asking for backtests. The only ground truth is real-time skill measured on real data."
            />
            <PainPoint
              label="04 · Diversity"
              title="The biggest name isn't always the best forecaster."
              body="Live rankings shift markedly from month to month. Even some of Europe's most established forecasting companies don't always lead the rankings, and in several months the best contributors were independent forecasters. A platform lets all of them participate, and rewards measured live accuracy."
            />
          </div>
        </Reveal>

        {/* Visualisation */}
        <Reveal as="section" id="live" className="mt-20 scroll-mt-24">
          <h2 className="text-[13px] uppercase tracking-[0.18em] text-slate-700 dark:text-gray-200 font-medium">
            Expectation vs. reality
          </h2>
          <p className="mt-3 text-sm sm:text-base text-slate-600 dark:text-gray-300 max-w-3xl leading-relaxed">
            Forecasting sounds easy until you watch a live day. On calm days everyone agrees and a single
            supplier looks fine. On the hard days (ramps, fronts, cloud cover) forecasters diverge and
            individual errors blow up. The Predico ensemble (in <span className="text-accent-500 font-medium">orange</span>)
            stays close to the observed signal (in black) in both regimes.
          </p>
          <div className="mt-8 space-y-10">
            <ForecastPanel title="Expectation" kind="expectation"/>
            <ForecastPanel title="Reality" kind="reality"/>
          </div>
        </Reveal>

        {/* How it works */}
        <Reveal as="section" id="how" className="mt-20 scroll-mt-24">
          <h2 className="text-[13px] uppercase tracking-[0.18em] text-slate-700 dark:text-gray-200 font-medium">
            How it works, in three steps
          </h2>
          <ol className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-900/10 dark:bg-gray-100/10 border border-slate-900/10 dark:border-gray-100/10">
            {[
              {
                step: '01',
                title: 'A market owner publishes a challenge',
                body: 'A TSO or buyer registers an asset and opens daily forecasting sessions with a target variable, horizon and resolution.',
              },
              {
                step: '02',
                title: 'Forecasters submit before gate closure',
                body: 'Anyone vetted (companies, research groups, individuals) submits a point and probabilistic forecast over HTTPS.',
              },
              {
                step: '03',
                title: 'Predico ensembles, scores, pays',
                body: 'Outliers are filtered, forecasts are combined into one operational ensemble, and incentives are paid on measured skill.',
              },
            ].map((s) => (
              <li key={s.step} className="bg-surface dark:bg-gray-870 p-6">
                <div className="text-[11px] uppercase tracking-[0.18em] text-brand-600 dark:text-brand-400 font-mono">
                  {s.step}
                </div>
                <div className="mt-2 text-sm font-medium text-slate-900 dark:text-white">{s.title}</div>
                <div className="mt-2 text-sm text-slate-600 dark:text-gray-300 leading-relaxed">{s.body}</div>
              </li>
            ))}
          </ol>
        </Reveal>

        {/* Results */}
        <Reveal as="section" id="results" className="mt-20 scroll-mt-24">
          <h2 className="text-[13px] uppercase tracking-[0.18em] text-slate-700 dark:text-gray-200 font-medium">
            First year of live operation with Elia
          </h2>
          <p className="mt-3 text-sm sm:text-base text-slate-600 dark:text-gray-300 max-w-3xl leading-relaxed">
            The proof-of-concept with the Belgian TSO went live in November 2024 on aggregated offshore
            wind, opened to external buyers and forecasters in March 2025, and extended to national solar
            PV. Sessions run daily with 15-minute resolution, P10/P50/P90 probabilistic forecasts, and
            gate closure at 10:00 CET. Across the full year, the ensemble outperformed the existing
            operational forecast on both point and probabilistic skill.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 border-y border-slate-900/10 dark:border-gray-100/10 divide-y sm:divide-y-0 sm:divide-x divide-slate-900/10 dark:divide-gray-100/10">
            <Metric
              value="6–8%"
              label="Improvement"
              type="Point forecast"
              description="Root Mean Squared Error"
              comparison="vs. operational baseline"
            />
            <Metric
              value="7–14%"
              label="Improvement"
              type="Probabilistic forecast"
              description="Mean Winkler Interval"
              comparison="80% prediction interval"
            />
            <Metric
              value="Open core"
              type="Forecast engine"
              description="AGPL-3.0 License"
              comparison={
                <a
                  href="https://github.com/INESCTEC/predico-collabforecast"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-700 dark:text-gray-300 underline underline-offset-2 hover:text-brand-600 dark:hover:text-brand-400 transition inline-flex items-center gap-1"
                >
                  GitHub
                  <span aria-hidden="true">↗</span>
                </a>
              }
            />
          </div>

          <div className="mt-12">
            <h3 className="text-base font-medium text-slate-900 dark:text-white">
              Rankings shift, month by month
            </h3>
            <p className="mt-2 text-sm sm:text-base text-slate-600 dark:text-gray-300 max-w-3xl leading-relaxed">
              Top-six monthly RMSE leaderboard from the first year of live operation. The Predico
              ensemble (in <span className="text-brand-600 dark:text-brand-400 font-medium">teal</span>)
              holds rank 1 or 2 in most months, while individual forecasters move up and down with
              weather regimes and model updates.
            </p>
            <div className="mt-6">
              <RankingTabs/>
            </div>
            <p className="mt-3 text-[11px] text-slate-500 dark:text-gray-400">
              <span className="font-mono">*</span> denotes the operational forecast provided by Elia's
              contracted supplier on each track. Other forecaster IDs are anonymised per the platform's
              governance rules.
            </p>
          </div>
        </Reveal>

        {/* References */}
        <Reveal as="section" id="references" className="mt-20 scroll-mt-24">
          <h2 className="text-[13px] uppercase tracking-[0.18em] text-slate-700 dark:text-gray-200 font-medium">
            References
          </h2>
          <ol className="mt-4 space-y-3 text-sm text-slate-600 dark:text-gray-300">
            <li className="flex gap-3">
              <span className="font-mono text-xs text-slate-400 dark:text-gray-500 pt-0.5 shrink-0">[1]</span>
              <span>
                Elia Group Innovation Hub.{' '}
                <a
                  href="https://innovation.eliagroup.eu/en/projects/predico-collaborative-forecasting-platform"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-slate-900 dark:hover:text-white transition"
                >
                  Predico: Collaborative Forecasting Platform
                </a>{' '}
                — project page, partners, and proof-of-concept overview.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="font-mono text-xs text-slate-400 dark:text-gray-500 pt-0.5 shrink-0">[2]</span>
              <span>
                Open-source forecast engine (AGPL-3.0):{' '}
                <a
                  href="https://github.com/INESCTEC/predico-collabforecast"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-slate-900 dark:hover:text-white transition"
                >
                  github.com/INESCTEC/predico-collabforecast
                </a>. The wider platform (REST API, web UI, marketplace) follows an
                open-core model; contact INESC TEC for commercial licensing.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="font-mono text-xs text-slate-400 dark:text-gray-500 pt-0.5 shrink-0">[3]</span>
              <span>
                Elia Open Data Platform: wind and solar measurement datasets used in the Belgian
                proof-of-concept,{' '}
                <a
                  href="https://opendata.elia.be/explore/dataset/ods031/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-slate-900 dark:hover:text-white transition"
                >
                  ods031
                </a>{' '}
                (wind),{' '}
                <a
                  href="https://opendata.elia.be/explore/dataset/ods032/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-slate-900 dark:hover:text-white transition"
                >
                  ods032
                </a>{' '}
                (solar).
              </span>
            </li>
          </ol>
        </Reveal>

        {/* CTA */}
        <Reveal as="section" className="mt-20 flex flex-wrap items-center justify-between gap-4 border-t border-slate-900/10 dark:border-gray-100/10 pt-10">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-gray-400">
              Get involved
            </div>
            <div className="mt-2 text-xl text-slate-900 dark:text-white font-light">
              Forecaster, market owner, or just curious. Join the live markets.
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <PublicButton to="/apply" variant="primary">
              Apply to join
            </PublicButton>
            <PublicButton to="/markets" variant="secondary">
              Browse markets
            </PublicButton>
          </div>
        </Reveal>
          </div>
        </div>
      </main>

      <PublicFooter/>
    </div>
  );
}
